import { queryCollection } from '#imports'
import { LAYERS, getCollectionName, type LayerName } from '../../utils/constants'

// I tag each item with its layer to make it easier to work with.
type Layered<T> = T & { _layer: LayerName }

function stripLayerPrefix(path: string): string {
    return path.replace(/^\/?(brand|region|base)/, '')
}

/**
 * Queries content across brand → region → base layers. Gets the highest priority layer for a given path.
 * Works with the dynamic collections created by `defineContentLayers`.
 */
export function useLayeredContent() {
    /** Deduplicate items across layers — keeps the highest-priority layer per slug. */
    function dedupeBySlug<T>(items: Layered<T>[]): Layered<T>[] {
        const seen = new Set<string>()
        return items.filter((item) => {
            const slug = stripLayerPrefix((item as any).stem ?? '')
            if (seen.has(slug)) return false
            seen.add(slug)
            return true
        })
    }
    async function safeQueryFirst(collection: string, path?: string) {
        try {
            let q = queryCollection(collection as any)
            if (path) q = q.path(path)
            return await q.first()
        }
        catch {
            return null
        }
    }

    async function safeQueryAll(collection: string) {
        try {
            return await queryCollection(collection as any).all()
        }
        catch {
            return []
        }
    }

    /** Tags each item with its layer. */
    function tagLayer<T>(items: T[], layer: LayerName): Layered<T>[] {
        return items.map(item => ({ ...item, _layer: layer })) as Layered<T>[]
    }

    /** Get a single item by path, cascading brand → region → base. */
    async function query<T = Record<string, unknown>>(
        contentType: string,
        path: string,
    ): Promise<Layered<T> | null> {
        let normalized = path.startsWith('/') ? path : `/${path}`
        normalized = stripLayerPrefix(normalized)

        for (const layer of LAYERS) {
            const collection = getCollectionName(contentType, layer)
            let result = await safeQueryFirst(collection, `/${layer}${normalized}`)

            if (!result && normalized === '/index') {
                result = await safeQueryFirst(collection, `/${layer}`)
            }

            if (result) return { ...result, _layer: layer } as Layered<T>
        }
        return null
    }

    /** Get all items from all layers, deduped by slug (brand wins over region wins over base). */
    async function queryAll<T = Record<string, unknown>>(
        contentType: string,
    ): Promise<Layered<T>[]> {
        const results: Layered<T>[] = []
        for (const layer of LAYERS) {
            const items = await safeQueryAll(getCollectionName(contentType, layer))
            results.push(...tagLayer<T>(items as T[], layer))
        }
        return dedupeBySlug(results)
    }

    /** Get items matching a category folder, ordered by sortOrder. Deduped by slug. */
    async function queryByCategory<T = Record<string, unknown>>(
        contentType: string,
        category: string,
    ): Promise<Layered<T>[]> {
        const results: Layered<T>[] = []
        for (const layer of LAYERS) {
            const collection = getCollectionName(contentType, layer)
            try {
                const items = await queryCollection(collection as any)
                    .where('stem', 'LIKE', `%/${category}/%`)
                    .order('sortOrder', 'ASC')
                    .all()
                results.push(...tagLayer<T>(items as T[], layer))
            }
            catch { /* collection may not exist in this layer */ }
        }
        return dedupeBySlug(results)
    }

    /** Search items by title across all layers. Deduped by slug. */
    async function searchByTitle<T = Record<string, unknown>>(
        contentType: string,
        searchTerm: string,
    ): Promise<Layered<T>[]> {
        if (!searchTerm || searchTerm.length < 2) return []

        const results: Layered<T>[] = []
        for (const layer of LAYERS) {
            const collection = getCollectionName(contentType, layer)
            try {
                const items = await queryCollection(collection as any)
                    .where('title', 'LIKE', `%${searchTerm}%`)
                    .all()
                results.push(...tagLayer<T>(items as T[], layer))
            }
            catch { /* collection may not exist in this layer */ }
        }
        return dedupeBySlug(results)
    }

    /** Find a single item by slug (filename) across all layers. */
    async function queryBySlug<T = Record<string, unknown>>(
        contentType: string,
        slug: string,
    ): Promise<Layered<T> | null> {
        for (const layer of LAYERS) {
            const collection = getCollectionName(contentType, layer)
            try {
                const items = await queryCollection(collection as any)
                    .where('stem', 'LIKE', `%/${slug}`)
                    .all()
                if (items.length > 0) {
                    return { ...items[0], _layer: layer } as Layered<T>
                }
            }
            catch { /* collection may not exist in this layer */ }
        }
        return null
    }

    return { query, queryAll, queryByCategory, queryBySlug, searchByTitle }
}

export default useLayeredContent
